import {
  BulkCreateShiftmanagementShiftShiftsInput,
  EmployeesEmployeeSelectorInput,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { toBreaksPayload } from 'modules/shift_management/lib/buildAddPlannedBreaksParameters'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'

import { BulkPatchProps } from './useEditShifts'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'

interface ReassignShiftProps {
  employeeSelection: EmployeesEmployeeSelectorInput
  shifts: Shift[]
  editShiftsParams: BulkPatchProps
  assignShiftsParams: BulkCreateShiftmanagementShiftShiftsInput[]
  isOriginalIncluded: boolean
  plannedBreakForms: PlannedBreakForm[]
  through: 'modal' | 'side_panel'
}

export const useReassignShifts = ({
  employeeSelection,
  shifts,
  editShiftsParams,
  assignShiftsParams,
  isOriginalIncluded,
  plannedBreakForms,
  through,
}: ReassignShiftProps) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()

  const [bulkDestroyShifts, { loading: loadingDestroyShifts }] = useMutation(
    ShiftManagementBulkDeleteShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
      update: (cache, { data }) => {
        removeDestroyedShiftsCache({
          cache,
          view,
          shifts: data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts || [],
        })
      },
    }
  )

  const [addShifts, { loading: loadingAddShifts }] = useMutation(
    ShiftManagementBulkCreateShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
      update(cache, { data }) {
        const newShifts = data?.shiftManagementMutations.bulkCreateShiftmanagementShift.shifts
        if (!newShifts) return

        updateCreatedShiftsCache({
          cache,
          view,
          shifts: newShifts,
        })
      },
    }
  )

  const onReassignShifts = async (closePortal?: ClosePortalFn) => {
    const assignedToModified = true
    const shiftIds = shifts.map((shift) => shift.id)
    // Get all shift rows that have shifts by employees affected by the edit action to update stats
    const shiftsEmployeeIds = uniq(shifts.map((shift: Shift) => shift.employeeId))

    const { name, startAt, endAt, notes, extraHours, locationId, workAreaId, mixedValueNotes } =
      editShiftsParams

    // After the employeeSelectorV2 refactor, we can select 'all' or X employee ids,
    // so we also include the original employee in the selection,
    // so we need to destroy the shifts of the original employee and recreate them.
    await bulkDestroyShifts({
      variables: { ids: shiftIds },
    })

    const result = await addShifts({
      variables: {
        shifts: assignShiftsParams,
        plannedBreaks: toBreaksPayload(plannedBreakForms),
        employeeSelection: employeeSelection,
      },
    })

    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      const shiftsData = handled.data?.shifts || []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId ?? '0',
        })
      )

      const { shiftsReferenceKeys } = buildEmployeeIds(view, shiftsRows, newShifts)

      const previousShiftsRows = shiftsRows.filter((row) => shiftsReferenceKeys.includes(row.key))
      await refreshStats(previousShiftsRows)

      const totalNumberOfAssignedEmployees = isOriginalIncluded
        ? shiftsEmployeeIds.length + assignShiftsParams.length
        : assignShiftsParams.length

      await trackEditShiftSubmit({
        shifts,
        name,
        startAt,
        endAt,
        notes,
        extraHours,
        locationId,
        workAreaId,
        mixedValueNotes,
        view,
        assignedToModified,
        originalEmployeeIncluded: isOriginalIncluded,
        totalNumberOfAssignedEmployees,
        through,
      })

      if (closePortal) closePortal()

      // Clear selection at the end to not remove the edit shifts button,
      // because the sidepanel and modal are rendered from it.
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.edit_shifts_success'),
      })
    }
  }

  return {
    onReassignShifts,
    submittingOnReassign: loadingDestroyShifts || loadingAddShifts,
  }
}
